import { useState } from 'react';
import { CreateGame } from '../utils/RemoteAuthority';
import { SeatColor, SeatColors } from './Constants/SeatColors';
import CollapsedCard from './CollpasedCard';
import { IsMobile } from '../utils/Mobile';
const { Modal } = require('react-bootstrap');
const { MixCounts, BaseGameCards, ChangedLeadersAndWonders, Versions } = require('@tta/engine/Constants/CardVersions');
const Leaders = require('@tta/engine/Constants/Leaders/Leaders');
const Wonders = require('@tta/engine/Constants/Wonders/Wonders');
const CardConverter = require('@tta/engine/Utilities/Converters/CardConverter');

// The game settings a new game can have with the expansion (the API's setup choices; see the engine's
// Constants/CardVersions.js, SetupFromChoices): what the players know of the leaders and wonders, and what they are
// picked from
const Visibility = [
    { value: 'public', label: 'Public', title: 'Everyone knows them from the start (the rulebook\'s recommended way to play)' },
    { value: 'byAge', label: 'Revealed each age', title: 'Each age\'s leaders and wonders are revealed as it begins' },
    { value: 'secret', label: 'Secret', title: 'No one knows them until they are drawn' },
];
const Selection = [
    { value: 'all', label: 'All', title: 'Picked at random from every leader and wonder of the base game and the expansion' },
    { value: 'new', label: 'New', title: 'All of the expansion\'s leaders and wonders. As the rulebook has it, this needs one base-game leader and one base-game wonder per age as well, picked at random' },
    { value: 'custom', label: 'Custom', title: 'Picked at random from the ones you choose' },
];

const Ages = ['Age A', 'Age I', 'Age II', 'Age III'];
const MaxPlayers = 4;

// A custom set: of each age's leaders and of its wonders, how many are left in, and how many a game of this many
// players needs ({ age, type, names, left, needed } for each)
const CustomGroups = (excluded, playerCount) => {
    const counts = MixCounts(Math.max(playerCount, 2));
    return Ages.flatMap(age => [['Leader', Leaders, counts.leaders], ['Wonder', Wonders, counts.wonders]].map(([type, table, needed]) => {
        const names = Object.keys(table).filter(name => table[name].age === age).sort((a, b) => a.localeCompare(b));
        return { age, type, names, left: names.filter(name => !excluded.includes(name)).length, needed };
    }));
};

// A select-able list item for a custom set: the card (hover for it) and whether it is in
function CustomCard({name, type, age, included, locked, OnToggle})
{
    return <label className={`tta-custom-card${included ? '' : ' out'}`} title={locked ? 'Needed: a game needs at least this many' : undefined}>
        <input type="checkbox" checked={included} disabled={locked} onChange={OnToggle}/>
        <CollapsedCard card={CardConverter({ type, name, age, id: -1 })}/>
        <span className="tta-muted">{BaseGameCards.has(name) ? 'Base' : 'Exp'}</span>
    </label>;
}

// A leader or wonder the expansion changed, and which version of it the game uses (none chosen: the expansion's); one
// left out of the game has no choice to make
function VersionChoice({name, version, excluded, OnChange})
{
    const type = Leaders[name] ? 'Leader' : 'Wonder';
    const age = (Leaders[name] ?? Wonders[name]).age;
    return <div className={`tta-version-choice${excluded ? ' out' : ''}`} title={excluded ? 'Left out of the game' : undefined}>
        <CollapsedCard card={CardConverter({ type, name, age, id: -1, version })}/>
        <div className="tta-segmented small">
            {[[Versions.Base, 'Base'], [Versions.Expansion, 'Expansion']].map(([value, label]) =>
                <button key={value} type="button" className={version === value ? 'on' : ''} disabled={excluded}
                    onClick={() => OnChange(value)}>{label}</button>)}
        </div>
    </div>;
}

// Choosing a custom set: every leader and wonder, all in to start with; each age keeps at least as many of each as a
// game of this many players needs. Then the version of each the expansion changed (overrides: the base game's ones, by
// name).
function CustomSetDialog({excluded, overrides, playerCount, OnChange, OnChangeOverrides, OnClose})
{
    const groups = CustomGroups(excluded, playerCount);
    const toggle = (name) => OnChange(excluded.includes(name) ? excluded.filter(other => other !== name) : [...excluded, name]);
    const setVersion = (name, version) => {
        const { [name]: _, ...rest } = overrides;
        OnChangeOverrides(version === Versions.Base ? { ...rest, [name]: version } : rest);
    };
    // (by age, leaders before wonders)
    const changed = ChangedLeadersAndWonders().sort((a, b) => {
        const [first, second] = [a, b].map(name => Leaders[name] ?? Wonders[name]);
        return Ages.indexOf(first.age) - Ages.indexOf(second.age) || (Leaders[a] ? 0 : 1) - (Leaders[b] ? 0 : 1) || a.localeCompare(b);
    });
    return <Modal show onHide={OnClose} centered size="lg" dialogClassName="tta-new-game-dialog tta-custom-set-dialog"
        backdropClassName="tta-stacked-backdrop" className="tta-stacked-modal">
        <Modal.Header closeButton>
            <Modal.Title>Leaders and wonders to pick from</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tta-lobby">
            <div className="tta-muted tta-field-note">
                Leave out any your group would rather not play with; the game still picks at random from the rest.
                Each age needs at least {groups[0].needed} leaders and {groups[1].needed} wonders for {Math.max(playerCount, 2)} players.
                {excluded.length > 0 && <> <button type="button" className="tta-link-button" onClick={() => OnChange([])}>Put them all back</button></>}
            </div>
            {Ages.map(age => <div key={age} className="tta-custom-age">
                <div className="tta-field-label">{age}</div>
                {groups.filter(group => group.age === age).map(group =>
                    <div key={group.type} className="tta-custom-group">
                        <div className="tta-muted tta-field-note">{group.type === 'Leader' ? 'Leaders' : 'Wonders'}: {group.left} of {group.names.length} in</div>
                        <div className="tta-custom-cards">
                            {group.names.map(name => {
                                const included = !excluded.includes(name);
                                return <CustomCard key={name} name={name} type={group.type} age={age} included={included}
                                    locked={included && group.left <= group.needed} OnToggle={() => toggle(name)}/>;
                            })}
                        </div>
                    </div>)}
            </div>)}
            <div className="tta-custom-age">
                <div className="tta-field-label">Versions</div>
                <div className="tta-muted tta-field-note">
                    The expansion changed these base-game leaders and wonders; play each in its expansion version or as
                    the base game has it.
                    {Object.keys(overrides).length > 0 && <> <button type="button" className="tta-link-button" onClick={() => OnChangeOverrides({})}>All the expansion's</button></>}
                </div>
                <div className="tta-version-choices">
                    {changed.map(name => <VersionChoice key={name} name={name} version={overrides[name] ?? Versions.Expansion}
                        excluded={excluded.includes(name)} OnChange={(version) => setVersion(name, version)}/>)}
                </div>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <button type="button" className="tta-primary-button" onClick={OnClose}>Done</button>
        </Modal.Footer>
    </Modal>;
}

// Starting a game: its title (needed), the people in it and the settings. The players: those in the game on the left (you
// first, and always in it), in seating order when the seats are not random (drag to reorder), each of the others on
// the right (filter, + to invite); they find it waiting for them, and cannot turn it down, only resign once it is
// going. Opens the game once it is started.
// initial (a rematch): { seats (the logins, in their seats), choices (the setup's, see the engine's ChoicesFromSetup) }
function NewGameDialog({ me, users, onClose, initial })
{
    const choices = initial?.choices ?? {};
    // (a rematch's players still here, you among them, first if you were not in it)
    const rematchSeats = (initial?.seats ?? []).filter(login => login && (login === me.login || users.some(user => user.login === login)));
    const [title, setTitle] = useState('');
    const [seated, setSeated] = useState(rematchSeats.includes(me.login) ? rematchSeats : [me.login, ...rematchSeats].slice(0, MaxPlayers));
    const [filter, setFilter] = useState('');
    const [dragging, setDragging] = useState(null);
    const [randomOrder, setRandomOrder] = useState(true);
    const [expansion, setExpansion] = useState(choices.expansion ?? true);
    const [reveal, setReveal] = useState(choices.reveal ?? 'public');
    const [pick, setPick] = useState(choices.pick ?? 'all');
    const [excluded, setExcluded] = useState(choices.excluded ?? []);
    const [overrides, setOverrides] = useState(choices.overrides ?? {});
    const [choosing, setChoosing] = useState(false);
    const [error, setError] = useState(null);
    const [starting, setStarting] = useState(false);

    const nameOf = (login) => users.find(user => user.login === login)?.name ?? (login === me.login ? me.name : login);
    const full = seated.length >= MaxPlayers;
    const others = users.filter(user => user.login !== me.login && !seated.includes(user.login))
        .filter(user => !filter.trim() || `${user.name} ${user.login}`.toLowerCase().includes(filter.trim().toLowerCase()))
        .sort((a, b) => a.name.localeCompare(b.name));
    const anyoneElse = users.some(user => user.login !== me.login);

    // Dragging a player over another moves them to that place
    const dragOver = (event, index) => {
        event.preventDefault();
        if (dragging === null || dragging === index)
        {
            return;
        }
        const order = [...seated];
        const [moved] = order.splice(dragging, 1);
        order.splice(index, 0, moved);
        setSeated(order);
        setDragging(index);
    };

    // On a phone, where dragging is unreliable: moving a player up or down a place with buttons instead
    const move = (index, by) => {
        const order = [...seated];
        const [moved] = order.splice(index, 1);
        order.splice(index + by, 0, moved);
        setSeated(order);
    };

    // A custom set too small for this many players (players added since it was chosen)
    const short = expansion && pick === 'custom'
        ? CustomGroups(excluded, seated.length).find(group => group.left < group.needed) : null;
    const problem = seated.length < 2 ? 'Add at least one player'
        : !title.trim() ? 'Give the game a title'
        : short ? `The custom set has ${short.left} ${short.age} ${short.type.toLowerCase()}s: ${seated.length} players need ${short.needed}`
        : null;

    const start = async () => {
        setStarting(true);
        setError(null);
        try {
            const setup = expansion ? { expansion, reveal, pick, ...(pick === 'custom' ? { excluded, overrides } : {}) } : { expansion: false };
            const id = await CreateGame(seated, setup, randomOrder, title.trim());
            window.location.href = `./?game=${id}`;
        } catch (e) {
            setError(e.message);
            setStarting(false);
        }
    };

    return <>
    <Modal show onHide={onClose} centered size="lg" dialogClassName="tta-new-game-dialog">
        <Modal.Header closeButton>
            <Modal.Title>{initial ? 'Rematch' : 'New game'}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="tta-lobby">
            <label className="tta-field">
                <span className="tta-field-label">Title</span>
                <input className="tta-input" value={title} maxLength={60} onChange={(e) => setTitle(e.target.value)} placeholder="Needed: what the game is called"/>
            </label>

            <div className="tta-field">
                <span className="tta-field-label">Players <span className="tta-muted">({seated.length} of 2–{MaxPlayers})</span></span>
                <div className="tta-player-lists">
                    <div className="tta-player-list">
                        <div className="tta-player-list-title">In this game</div>
                        {seated.map((login, index) =>
                            <div key={login} className={`tta-player-row${dragging === index ? ' dragging' : ''}`} draggable={!IsMobile}
                                onDragStart={() => setDragging(index)} onDragOver={(event) => dragOver(event, index)} onDragEnd={() => setDragging(null)}
                                title={IsMobile ? undefined : 'Drag to change the order'}>
                                {IsMobile
                                    ? <span className="tta-move-buttons">
                                        <button type="button" className="tta-row-button" disabled={index === 0} onClick={() => move(index, -1)} aria-label="Move up">▲</button>
                                        <button type="button" className="tta-row-button" disabled={index === seated.length - 1} onClick={() => move(index, 1)} aria-label="Move down">▼</button>
                                    </span>
                                    : <span className="tta-drag-handle">⋮⋮</span>}
                                {randomOrder ? <span className="tta-avatar small">{nameOf(login).slice(0, 1).toUpperCase()}</span>
                                    : <span className="tta-seat-dot" style={{ background: SeatColor(index, SeatColors(seated.length, seated.indexOf(me.login), me.color)) }}/>}
                                <span className="tta-player-row-name">{!randomOrder && `${index + 1}. `}{nameOf(login)}{login === me.login && <span className="tta-muted"> (you)</span>}</span>
                                {login !== me.login && <button type="button" className="tta-row-button" title="Take them out of the game"
                                    onClick={() => setSeated(seated.filter(other => other !== login))}>−</button>}
                            </div>)}
                    </div>
                    <div className="tta-player-list">
                        <div className="tta-player-list-title">Everyone else</div>
                        <input className="tta-input tta-player-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name"/>
                        <div className="tta-player-list-scroll">
                            {!anyoneElse && <div className="tta-muted tta-field-note">No one else has signed up yet: the people you can play with show here once they have.</div>}
                            {anyoneElse && others.length === 0 && <div className="tta-muted tta-field-note">{filter.trim() ? 'No one matches.' : 'Everyone is in.'}</div>}
                            {others.map(user =>
                                <div key={user.login} className="tta-player-row" title={user.login}>
                                    <span className="tta-avatar small">{user.name.slice(0, 1).toUpperCase()}</span>
                                    <span className="tta-player-row-name">{user.name}</span>
                                    <button type="button" className="tta-row-button" disabled={full}
                                        title={full ? `${MaxPlayers} players at most` : 'Add them to the game'}
                                        onClick={() => setSeated([...seated, user.login])}>+</button>
                                </div>)}
                        </div>
                    </div>
                </div>
                <div className="tta-muted tta-field-note">They'll find the game waiting for them on their front page.</div>
            </div>

            <div className="tta-field">
                <span className="tta-field-label">Seating</span>
                <div className="tta-segmented">
                    <button type="button" className={randomOrder ? 'on' : ''} onClick={() => setRandomOrder(true)}>Random</button>
                    <button type="button" className={!randomOrder ? 'on' : ''} onClick={() => setRandomOrder(false)}>In the order above</button>
                </div>
            </div>

            <div className="tta-field">
                <span className="tta-field-label">Game settings</span>
                <label className="tta-switch">
                    <input type="checkbox" checked={expansion} onChange={(e) => setExpansion(e.target.checked)}/>
                    <span>Use the expansion <span className="tta-muted">(New Leaders and Wonders)</span></span>
                </label>
                {!expansion && <div className="tta-muted tta-field-note">The base game, with its own leaders and wonders.</div>}
                {expansion && <>
                    <div className="tta-setting">
                        <div className="tta-setting-label">Leader and wonder visibility</div>
                        <div className="tta-setup-choices">
                            {Visibility.map(choice => <Choice key={choice.value} name="reveal" choice={choice} value={reveal} OnChange={setReveal}/>)}
                        </div>
                    </div>
                    <div className="tta-setting">
                        <div className="tta-setting-label">Leader and wonder selection</div>
                        <div className="tta-setup-choices">
                            {Selection.map(choice => <Choice key={choice.value} name="pick" choice={choice} value={pick} OnChange={setPick}>
                                {choice.value === 'custom' && pick === 'custom' && <span className="tta-custom-summary">
                                    {excluded.length ? `${excluded.length} left out` : 'Nothing left out yet'}
                                    {/* (versions of cards left out do not count) */}
                                    {Object.keys(overrides).filter(name => !excluded.includes(name)).length > 0
                                        && `, ${Object.keys(overrides).filter(name => !excluded.includes(name)).length} in the base game's version`}
                                    {' '}<button type="button" className="tta-link-button" onClick={(e) => { e.preventDefault(); setChoosing(true); }}>Choose…</button>
                                </span>}
                            </Choice>)}
                        </div>
                    </div>
                </>}
            </div>
            {error && <div className="tta-lobby-error">{error}</div>}
        </Modal.Body>
        <Modal.Footer>
            {problem && seated.length >= 2 && <span className="tta-lobby-error tta-footer-problem">{problem}</span>}
            <button type="button" className="tta-ghost-button" onClick={onClose}>Cancel</button>
            <button type="button" className="tta-primary-button" disabled={!!problem || starting} onClick={start}>
                {starting ? 'Starting…' : seated.length < 2 ? problem : 'Start game'}
            </button>
        </Modal.Footer>
    </Modal>
    {choosing && <CustomSetDialog excluded={excluded} overrides={overrides} playerCount={seated.length} OnChange={setExcluded}
        OnChangeOverrides={setOverrides} OnClose={() => setChoosing(false)}/>}
    </>;
}

// One of a setting's choices, with what it means under it
const Choice = ({name, choice, value, OnChange, children}) =>
    <label className={`tta-setup-choice${value === choice.value ? ' on' : ''}`}>
        <input type="radio" name={name} value={choice.value} checked={value === choice.value} onChange={() => OnChange(choice.value)}/>
        <span>
            <span className="tta-setup-choice-label">{choice.label}</span>
            <span className="tta-muted">{choice.title}</span>
            {children}
        </span>
    </label>;

export default NewGameDialog;
